import * as React from 'react';
import { Link } from 'react-router-dom';

import styled from '@emotion/styled';

import { Box, COLORS } from '@clutter/clean';

import { itemsWithConditionNotesURL } from '@portal/config/routes';

/** Figma visibility icon size (`18:1782`). */
const EYE_PX = 20;

/**
 * Figma Item Conditions `18:1778` (dev handoff):
 * https://www.figma.com/design/HA8WTf80Hhmxg6EZdBc8Dn/Item-Conditions--Pre-existing---Clutter-caused-?node-id=18-1775&m=dev
 * — `functional/border-grey` fill (#ebeced), radius ~29px, Basier Medium label, visibility eye.
 *
 * `react-router`’s `Link` does not always receive the Emotion `css` prop, so a styled
 * component keeps the border-grey background from being stripped by global anchor rules.
 */
const ChipLink = styled(Link)`
  display: inline-flex;
  align-items: flex-end;
  gap: 6.414px;
  line-height: 1.2;
  padding: 6.414px 10.417px;
  border-radius: 28.865px;
  text-decoration: none;
  color: #000;
  background-color: ${COLORS.grayBorder};

  &,
  &:hover,
  &:focus,
  &:active,
  &:visited {
    color: #000;
    text-decoration: none;
    background-color: ${COLORS.grayBorder};
  }
`;

/** Figma visibility — fill `#1C1B1F` from asset (mask omitted so mask ids never collide). */
function VisibilityEyeIcon(): React.ReactElement {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width={EYE_PX} height={EYE_PX} viewBox="0 0 11 11" fill="none" aria-hidden>
      <path
        d="M6.96061 6.73145C7.36165 6.3304 7.56218 5.84342 7.56218 5.27051C7.56218 4.69759 7.36165 4.21061 6.96061 3.80957C6.55957 3.40853 6.07259 3.20801 5.49967 3.20801C4.92676 3.20801 4.43978 3.40853 4.03874 3.80957C3.6377 4.21061 3.43717 4.69759 3.43717 5.27051C3.43717 5.84342 3.6377 6.3304 4.03874 6.73145C4.43978 7.13249 4.92676 7.33301 5.49967 7.33301C6.07259 7.33301 6.55957 7.13249 6.96061 6.73145ZM4.62311 6.14707C4.38249 5.90645 4.26217 5.61426 4.26217 5.27051C4.26217 4.92676 4.38249 4.63457 4.62311 4.39395C4.86374 4.15332 5.15592 4.03301 5.49967 4.03301C5.84342 4.03301 6.13561 4.15332 6.37624 4.39395C6.61686 4.63457 6.73717 4.92676 6.73717 5.27051C6.73717 5.61426 6.61686 5.90645 6.37624 6.14707C6.13561 6.3877 5.84342 6.50801 5.49967 6.50801C5.15592 6.50801 4.86374 6.3877 4.62311 6.14707ZM2.45176 7.77415C1.53509 7.15158 0.870508 6.31704 0.458008 5.27051C0.870508 4.22398 1.53509 3.38943 2.45176 2.76686C3.36842 2.14429 4.3844 1.83301 5.49967 1.83301C6.61495 1.83301 7.63092 2.14429 8.54759 2.76686C9.46426 3.38943 10.1288 4.22398 10.5413 5.27051C10.1288 6.31704 9.46426 7.15158 8.54759 7.77415C7.63092 8.39672 6.61495 8.70801 5.49967 8.70801C4.3844 8.70801 3.36842 8.39672 2.45176 7.77415ZM7.87728 7.10957C8.59915 6.65506 9.15106 6.04204 9.53301 5.27051C9.15106 4.49898 8.59915 3.88596 7.87728 3.43145C7.1554 2.97693 6.36287 2.74967 5.49967 2.74967C4.63648 2.74967 3.84395 2.97693 3.12207 3.43145C2.4002 3.88596 1.84829 4.49898 1.46634 5.27051C1.84829 6.04204 2.4002 6.65506 3.12207 7.10957C3.84395 7.56408 4.63648 7.79134 5.49967 7.79134C6.36287 7.79134 7.1554 7.56408 7.87728 7.10957Z"
        fill="#1C1B1F"
      />
    </svg>
  );
}

export const ConditionNotesChipButton: React.FC = () => (
  <ChipLink to={itemsWithConditionNotesURL()}>
    <Box
      as="span"
      css={{
        position: 'relative',
        flexShrink: 0,
        width: EYE_PX,
        height: EYE_PX,
        display: 'block',
        lineHeight: 0,
      }}
    >
      <VisibilityEyeIcon />
    </Box>
    <Box
      as="span"
      css={{
        display: 'inline',
        color: '#000',
        fontFeatureSettings: "'liga' off, 'clig' off",
        fontFamily: '"Basier Square", sans-serif',
        fontSize: '18px',
        fontStyle: 'normal',
        fontWeight: 500,
        lineHeight: '120%',
        whiteSpace: 'nowrap',
      }}
    >
      Items for your review
    </Box>
  </ChipLink>
);
